/**
 * Browser-side helpers that call the ChatGPT Ads loader's window hooks
 * (defined by lib/openai-pixel.ts). Plain module, no React: it is imported by
 * lib/meta-funnel.ts, which server routes import too. Every helper is a silent
 * no-op on the server and until the consent-gated loader has initialised.
 */

export interface OpenAIFunnelFire {
  cents?: number;
  currency?: string;
  contentIds?: string[];
}

type OaiWindow = {
  __oaiTrack?: (ev: string, d: OpenAIFunnelFire, eid: string) => void;
  __oaiIdentify?: (emailSha256: string) => void;
  __oaiMeasure?: (name: string, data: Record<string, unknown>, opts: Record<string, unknown>) => void;
};

function oai(): OaiWindow | null {
  return typeof window === "undefined" ? null : (window as unknown as OaiWindow);
}

/** Funnel event under its Meta name, with the Meta event id. */
export function fireOpenAI(metaEvent: string, d: OpenAIFunnelFire, eventId: string): void {
  try {
    oai()?.__oaiTrack?.(metaEvent, d, eventId);
  } catch {
    /* tracking must never break checkout */
  }
}

/** SHA-256 hex of the normalized email (trim + lowercase), or "" when unavailable. */
export async function openaiEmailSha256(email: string | undefined): Promise<string> {
  const v = (email || "").trim().toLowerCase();
  if (!v.includes("@") || typeof crypto === "undefined" || !crypto.subtle) return "";
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

export function identifyOpenAI(emailSha256: string): void {
  if (emailSha256) oai()?.__oaiIdentify?.(emailSha256);
}

export function measureOpenAI(name: string, data: Record<string, unknown>, opts: Record<string, unknown>): void {
  try {
    oai()?.__oaiMeasure?.(name, data, opts);
  } catch {
    /* tracking must never break the page */
  }
}
